import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useRequestBodies } from './useRequestBodies';
import { useStreamingBody } from './useStreamingBody';
import {
  encodeBinaryBody,
  decodeBinaryBodyForDisplay,
} from '../electron-shared/utils/binary-body';

const MAX_INLINE_DISPLAY_SIZE = 100 * 1024;
const STREAMING_THRESHOLD = 2 * 1024 * 1024; // 2MB - use streaming for larger bodies

function formatTruncatedInlineBody(
  rawInline: string,
  maxDisplaySize: number,
): string {
  const encoder = new TextEncoder();
  const totalBytes = encoder.encode(rawInline).length;
  const displayedBytes = encoder.encode(
    rawInline.slice(0, maxDisplaySize),
  ).length;
  const omittedBytes = Math.max(0, totalBytes - displayedBytes);
  const omittedKB = (omittedBytes / 1024).toFixed(0);

  return (
    rawInline.slice(0, maxDisplaySize) +
    `\n\n... [Truncated: ${omittedKB}KB more - click "See more" to view]`
  );
}

type UseLazyBodyOptions = {
  bodyRef?: string;
  bodyPreview?: string;
  inlineBody?: string;
  bodyStorageType?: 'inline' | 'file';
  bodyContentType?: 'text' | 'binary' | 'multipart';
  bodySize?: number;
};

export type UseLazyBodyResult = {
  body: string;
  isLoading: boolean;
  isLargeBody: boolean;
  isFullyLoaded: boolean;
  /** Load the full body with progressive streaming. */
  loadFullBody: () => Promise<string | null>;
  error: string | null;
  /** Progressive loading - whether there are more lines to show */
  hasMoreLines: boolean;
  /** Progressive loading - load more lines into view */
  loadMoreLines: () => void;
  /** Number of lines currently displayed */
  displayedLineCount: number;
  /** Total number of lines available */
  totalLineCount: number;
  /** Loading progress from 0 to 1 */
  loadProgress: number;
};

export function useLazyBody({
  bodyRef,
  bodyPreview,
  inlineBody,
  bodyStorageType,
  bodyContentType,
  bodySize,
}: UseLazyBodyOptions): UseLazyBodyResult {
  const {
    fetchLargeBody,
    fetchLargeBodyAsBinary,
    getLargeBody,
    isLargeBodyLoading,
  } = useRequestBodies();

  const [fullBody, setFullBody] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);
  const [useStreaming, setUseStreaming] = useState(false);
  const [showFullInline, setShowFullInline] = useState(false);

  const isFileStored = bodyStorageType === 'file' && !!bodyRef;
  const isBinaryBody =
    bodyContentType === 'binary' || bodyContentType === 'multipart';
  const inlineBodySize = inlineBody?.length ?? 0;
  const isInlineTruncated =
    !isFileStored && inlineBodySize > MAX_INLINE_DISPLAY_SIZE;
  const isLargeBody = isFileStored || isInlineTruncated;

  const streaming = useStreamingBody(useStreaming ? bodyRef : undefined);

  const cachedBody = !isBinaryBody && bodyRef ? getLargeBody(bodyRef) : null;

  useEffect(() => {
    if (cachedBody && !fullBody) {
      setFullBody(cachedBody);
    }
  }, [cachedBody, fullBody]);

  useEffect(() => {
    setFullBody(null);
    setError(null);
    setUseStreaming(false);
    setShowFullInline(false);
    streaming.reset();
  }, [bodyRef, inlineBody]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFullBody = useCallback(async (): Promise<string | null> => {
    // Case 1: Inline body that's truncated - just show it
    if (isInlineTruncated && !isFileStored) {
      setShowFullInline(true);

      return inlineBody || null;
    }

    // Case 2: Already loaded via streaming
    if (streaming.isFullyFetched && streaming.displayedContent) {
      return streaming.displayedContent;
    }

    // Case 3: Already have full body cached
    if (fullBody) return fullBody;

    if (!bodyRef || loadingRef.current) return null;

    // Decide: use streaming for large bodies (>2MB), one-shot for smaller
    const shouldUseStreaming =
      isFileStored &&
      !isBinaryBody &&
      typeof window.electronAPI?.bodyStorage?.getChunked === 'function' &&
      (bodySize ?? 0) >= STREAMING_THRESHOLD;

    if (shouldUseStreaming) {
      // Case 4: Large file - use streaming for progress indication
      setUseStreaming(true);
      await new Promise((resolve) => setTimeout(resolve, 0));
      streaming.startStreaming();

      return null;
    }

    // Case 5: Small-medium file (<2MB) - use one-shot loading (faster)
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const loadedBody = isBinaryBody
        ? await fetchLargeBodyAsBinary(bodyRef)
        : await fetchLargeBody(bodyRef);

      if (loadedBody) {
        const displayBody = isBinaryBody
          ? decodeBinaryBodyForDisplay(
              encodeBinaryBody(Buffer.from(loadedBody, 'base64')),
            )
          : loadedBody;

        setFullBody(displayBody);

        return displayBody;
      } else {
        setError('Failed to load body content');

        return null;
      }
    } catch (err) {
      setError(`Error loading body: ${err}`);

      return null;
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [
    bodyRef,
    bodySize,
    fullBody,
    fetchLargeBody,
    fetchLargeBodyAsBinary,
    isFileStored,
    isBinaryBody,
    isInlineTruncated,
    inlineBody,
    streaming,
  ]);

  const body = useMemo(() => {
    if (useStreaming && (streaming.displayedContent || streaming.isLoading)) {
      return streaming.displayedContent || bodyPreview || '';
    }

    if (isFileStored) {
      return fullBody || bodyPreview || '';
    }

    const rawInline = inlineBody || '';

    if (isInlineTruncated && !showFullInline) {
      return formatTruncatedInlineBody(rawInline, MAX_INLINE_DISPLAY_SIZE);
    }

    return rawInline;
  }, [
    useStreaming,
    streaming.displayedContent,
    streaming.isLoading,
    bodyPreview,
    isFileStored,
    fullBody,
    inlineBody,
    isInlineTruncated,
    showFullInline,
  ]);

  const combinedIsLoading =
    isLoading ||
    streaming.isLoading ||
    (bodyRef ? isLargeBodyLoading(bodyRef) : false);

  const isFullyLoaded = useStreaming
    ? streaming.isFullyFetched
    : isFileStored
      ? !!fullBody
      : isInlineTruncated
        ? showFullInline
        : true;

  return {
    body,
    isLoading: combinedIsLoading,
    isLargeBody,
    isFullyLoaded,
    loadFullBody,
    error: error || streaming.error,
    hasMoreLines: useStreaming ? streaming.hasMoreLines : false,
    loadMoreLines: streaming.loadMoreLines,
    displayedLineCount: useStreaming ? streaming.displayedLineCount : 0,
    totalLineCount: useStreaming ? streaming.totalLineCount : 0,
    loadProgress: useStreaming ? streaming.loadProgress : 0,
  };
}
